/**
 * Safe-issue report redaction builder.
 *
 * Pure functions only. Builds an allow-listed, redacted diagnostic payload for an
 * internal "something went wrong" report. It NEVER includes file contents,
 * serials, customer contacts, credentials, signed URLs, SQL, or stack traces.
 * The output is safe to email to an admin and to paste into a diagnostic prompt.
 */

export type SafeIssueInput = {
  route: string;
  workflowStage: string;
  safeErrorCode: string;
  operation: string;
  buildCommit?: string | null;
  projectProcurementId?: number | null;
  /** Inventory replenishment requests have no project/proposal to attach to. */
  inventoryRequestId?: number | null;
  /**
   * Legacy retail handover id when reporting from `/procurement/handovers/:id`
   * mapping failure — no Portal procurement id may exist yet.
   * Persisted via counts.legacyHandoverId (no fabricated procurement id).
   */
  legacyHandoverId?: number | null;
  proposalId?: number | null;
  documentId?: number | null;
  reporterUserId?: number | null;
  /** Non-sensitive counts only (e.g. { proposedLines: 12, unmatched: 3 }). */
  counts?: Record<string, unknown> | null;
  /** A short human note; will be redacted before inclusion. */
  note?: string | null;
};

export type SafeIssueReport = {
  route: string;
  workflowStage: string;
  safeErrorCode: string;
  operation: string;
  buildCommit: string | null;
  projectProcurementId: number | null;
  inventoryRequestId: number | null;
  proposalId: number | null;
  documentId: number | null;
  reporterUserId: number | null;
  counts: Record<string, number>;
  redactedSummary: string;
};

// Patterns for values that must never appear in a report.
const EMAIL_RE = /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g;
const URL_RE = /\b(?:https?|s3|r2|ftp):\/\/[^\s)]+/gi;
const SIGNED_URL_QUERY_RE = /[?&](?:x-amz-[^\s&]+|signature|sig|token|expires)=[^\s&]+/gi;
const PHONE_RE = /\+?\d[\d\s\-()]{6,}\d/g;
const CREDENTIAL_RE = /\b(?:password|passwd|secret|api[_-]?key|access[_-]?key|token|bearer|authorization)\b\s*[:=]\s*\S+/gi;
const SQL_RE = /\b(select|insert|update|delete|drop|alter|create|truncate|union|where|from|join)\b\s+\S+/gi;
// Stack frame lines like "at fn (/path/file.ts:12:3)" or "file.ts:12".
const STACK_FRAME_RE = /\bat\s+.+?\(?[^\s]+:\d+:\d+\)?/gi;
const SQL_QUOTED_RE = /`[^`]*`/g;

const CONTROL_LABEL = "[redacted]";

/**
 * Redact a free-text string: strip emails, URLs, signed-URL query params,
 * phone numbers, credentials, SQL fragments and stack frames, then clamp length.
 * Also removes any long alphanumeric tokens that look like serials / keys.
 */
export function redactText(raw: string | null | undefined, maxLength = 500): string {
  let s = String(raw ?? "");
  if (!s) return "";
  s = s.replace(SIGNED_URL_QUERY_RE, CONTROL_LABEL);
  s = s.replace(URL_RE, CONTROL_LABEL);
  s = s.replace(EMAIL_RE, CONTROL_LABEL);
  s = s.replace(CREDENTIAL_RE, CONTROL_LABEL);
  s = s.replace(STACK_FRAME_RE, CONTROL_LABEL);
  s = s.replace(SQL_RE, CONTROL_LABEL);
  s = s.replace(SQL_QUOTED_RE, CONTROL_LABEL);
  s = s.replace(PHONE_RE, CONTROL_LABEL);
  // Long opaque tokens (>= 10 chars, mixed digits+letters) — likely serials / ids / keys.
  s = s.replace(/\b(?=[A-Za-z0-9]*\d)(?=[A-Za-z0-9]*[A-Za-z])[A-Za-z0-9]{10,}\b/g, CONTROL_LABEL);
  s = s.replace(/\s+/g, " ").trim();
  return s.slice(0, maxLength);
}

/** Keep only finite numeric counts; drop anything non-numeric. */
export function sanitizeCounts(counts: Record<string, unknown> | null | undefined): Record<string, number> {
  const out: Record<string, number> = {};
  if (!counts) return out;
  for (const [key, value] of Object.entries(counts)) {
    const cleanKey = String(key).replace(/[^A-Za-z0-9_]/g, "").slice(0, 48);
    if (!cleanKey) continue;
    const n = Number(value);
    if (Number.isFinite(n)) out[cleanKey] = n;
  }
  return out;
}

function safeIdentifier(raw: string | null | undefined, maxLength: number): string {
  return String(raw ?? "")
    .replace(/[^A-Za-z0-9_:/.\- ]/g, "")
    .trim()
    .slice(0, maxLength);
}

/** Build the redacted, allow-listed report from raw inputs. */
export function buildSafeIssueReport(input: SafeIssueInput): SafeIssueReport {
  const counts = sanitizeCounts({
    ...(input.counts ?? {}),
    ...(input.legacyHandoverId != null && Number.isFinite(Number(input.legacyHandoverId))
      ? { legacyHandoverId: Number(input.legacyHandoverId) }
      : {}),
  });
  const summaryParts = [
    `stage=${safeIdentifier(input.workflowStage, 64)}`,
    `op=${safeIdentifier(input.operation, 128)}`,
    `code=${safeIdentifier(input.safeErrorCode, 64)}`,
  ];
  if (input.legacyHandoverId != null && Number.isFinite(Number(input.legacyHandoverId))) {
    summaryParts.push(`legacyHandoverId=${Math.trunc(Number(input.legacyHandoverId))}`);
  }
  if (input.note) {
    const note = redactText(input.note, 300);
    if (note) summaryParts.push(`note=${note}`);
  }
  const countSummary = Object.entries(counts)
    .map(([k, v]) => `${k}:${v}`)
    .join(", ");
  if (countSummary) summaryParts.push(`counts=${countSummary}`);

  return {
    route: safeIdentifier(input.route, 191),
    workflowStage: safeIdentifier(input.workflowStage, 64),
    safeErrorCode: safeIdentifier(input.safeErrorCode, 64),
    operation: safeIdentifier(input.operation, 128),
    buildCommit: input.buildCommit ? safeIdentifier(input.buildCommit, 64) : null,
    projectProcurementId: toNullableInt(input.projectProcurementId),
    inventoryRequestId: toNullableInt(input.inventoryRequestId),
    proposalId: toNullableInt(input.proposalId),
    documentId: toNullableInt(input.documentId),
    reporterUserId: toNullableInt(input.reporterUserId),
    counts,
    redactedSummary: summaryParts.join(" | ").slice(0, 1000),
  };
}

/**
 * A copy/paste diagnostic prompt for engineers — carries only the redacted,
 * allow-listed fields plus a deep link. Never includes sensitive material.
 */
export function buildDiagnosticPrompt(report: SafeIssueReport, deepLink?: string | null): string {
  const lines = [
    "Clarity Portal — safe issue diagnostic (redacted, no customer data).",
    `Route: ${report.route}`,
    `Workflow stage: ${report.workflowStage}`,
    `Operation: ${report.operation}`,
    `Error code: ${report.safeErrorCode}`,
    report.buildCommit ? `Build: ${report.buildCommit}` : null,
    report.projectProcurementId != null ? `Procurement id: ${report.projectProcurementId}` : null,
    report.inventoryRequestId != null ? `Inventory request id: ${report.inventoryRequestId}` : null,
    report.counts.legacyHandoverId != null
      ? `Legacy handover id: ${report.counts.legacyHandoverId}`
      : null,
    report.proposalId != null ? `Proposal id: ${report.proposalId}` : null,
    report.documentId != null ? `Document id: ${report.documentId}` : null,
    Object.keys(report.counts).length > 0 ? `Counts: ${JSON.stringify(report.counts)}` : null,
    report.redactedSummary ? `Summary: ${report.redactedSummary}` : null,
    deepLink ? `Deep link: ${safeIdentifier(deepLink, 512)}` : null,
  ].filter(Boolean);
  return lines.join("\n");
}

function toNullableInt(v: unknown): number | null {
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? Math.trunc(n) : null;
}
